import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components/ui';
import type { MatchMode, MatchResult, MatchSummary, ReattachTarget } from './types';

import './ReviewTags.scss';

const unmatchedOf = (results: MatchResult[]) => results.filter((r) => r.disposition === 'unmatched');
const needsDecisionOf = (results: MatchResult[]) => results.filter((r) => r.disposition === 'unmatched' || r.partial);

type RowState = 'open' | 'pending' | 'placed' | 'removed';

const rowState = (tagId: string, pendingTagId: string | null, dropped: Set<string>, reattached: Map<string, ReattachTarget>): RowState => {
  if (reattached.has(tagId)) return 'placed';
  if (dropped.has(tagId)) return 'removed';
  if (pendingTagId === tagId) return 'pending';
  return 'open';
};

interface ReviewVerdictProps {
  results?: MatchResult[];
  summary?: MatchSummary;
  reattached: Map<string, ReattachTarget>;
  dropped?: Set<string>;
  mode?: MatchMode;
}

export function ReviewVerdict({ results, summary, reattached, dropped = new Set(), mode = 'label' }: ReviewVerdictProps) {
  const { t } = useTranslation('reference-data');
  if (!results || results.length === 0) return null;

  const droppedPartial = results.filter((r) => r.partial && dropped.has(r.tagId)).length;
  const remaining = unmatchedOf(results).filter((r) => !reattached.has(r.tagId)).length + droppedPartial;
  const flagged = (summary?.flagged ?? 0) + reattached.size - droppedPartial;
  const byLabel = mode === 'label';

  return (
    <div className="review-verdict" data-testid="review-tags">
      <div className="review-verdict__line">
        <Typography variant="body-7" fontWeight="semibold" className="review-verdict__sentence">
          {t('review-tags.verdict', { total: results.length })}
        </Typography>
        <span className="review-verdict__pill review-verdict__pill--kept" data-testid="review-tags-carried">
          <span className="review-verdict__dot" aria-hidden="true" />
          {summary?.carried ?? 0} {t('review-tags.carried')}
        </span>
        {(byLabel || flagged > 0) && (
          <span className="review-verdict__pill review-verdict__pill--flagged" data-testid="review-tags-flagged">
            <span className="review-verdict__dot" aria-hidden="true" />
            {flagged} {t('review-tags.flagged')}
          </span>
        )}
        {(byLabel || remaining > 0) && (
          <span className="review-verdict__pill review-verdict__pill--unplaced" data-testid="review-tags-unmatched">
            <span className="review-verdict__dot" aria-hidden="true" />
            {remaining} {t('review-tags.unmatched')}
          </span>
        )}
        {byLabel && (
          <span className="review-verdict__pill review-verdict__pill--changed">
            <span className="review-verdict__dot" aria-hidden="true" />
            {t('review-tags.changed-value')}
          </span>
        )}
      </div>
      <Typography variant="body-3" className="review-verdict__explainer" color="#6b7785">
        {t(byLabel ? 'review-tags.explainer' : 'review-tags.explainer-position')}
      </Typography>
    </div>
  );
}

interface ReviewTagsProps {
  results?: MatchResult[];
  pendingTagId: string | null;
  dropped: Set<string>;
  reattached: Map<string, ReattachTarget>;
  onStartReattach: (tagId: string) => void;
  onCancelReattach: () => void;
  onDrop: (tagId: string) => void;
  onUndo: (tagId: string) => void;
  conceptLabels?: Record<string, string>;
}

export function ReviewTags({
  results,
  pendingTagId,
  dropped,
  reattached,
  onStartReattach,
  onCancelReattach,
  onDrop,
  onUndo,
  conceptLabels = {},
}: ReviewTagsProps) {
  const { t } = useTranslation(['common', 'reference-data']);
  if (!results || results.length === 0) return null;
  const rows = needsDecisionOf(results);
  if (rows.length === 0) return null;

  return (
    <section className="review-tags" data-testid="review-tags-list">
      <Typography variant="body-5" fontWeight="semibold" className="review-tags__heading">
        {t('reference-data:review-tags.fix-heading')}
      </Typography>
      <div className="review-tags__list">
        <table className="review-tags__table">
          <thead>
            <tr>
              <th scope="col">{t('reference-data:review-tags.col-concept')}</th>
              <th scope="col">{t('reference-data:review-tags.col-row')}</th>
              <th scope="col">{t('reference-data:review-tags.col-column')}</th>
              <th scope="col" className="review-tags__value">
                {t('reference-data:review-tags.col-value')}
              </th>
              <th scope="col" aria-label={t('reference-data:review-tags.col-actions')} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const state = rowState(r.tagId, pendingTagId, dropped, reattached);
              return (
                <tr key={r.tagId} className={`review-tags__row review-tags__row--${state}`} data-testid={`review-tag-${r.tagId}`}>
                  <td className="review-tags__concept">{conceptLabels[r.tagId]}</td>
                  <td className="review-tags__label">{r.oldLabel}</td>
                  <td className="review-tags__period">{r.oldHeader}</td>
                  <td className="review-tags__value">{r.oldValue}</td>
                  <td>
                    <div className="review-tags__actions">
                      {state === 'open' && r.partial && (
                        <>
                          <span className="review-tags__prompt">{t('reference-data:review-tags.partial', r.partial)}</span>
                          <button type="button" className="button clear review-tags__remove" onClick={() => onDrop(r.tagId)}>
                            {t('reference-data:review-tags.drop')}
                          </button>
                        </>
                      )}
                      {state === 'open' && !r.partial && (
                        <>
                          <button type="button" className="button clear" onClick={() => onStartReattach(r.tagId)}>
                            {t('reference-data:review-tags.reattach')}
                          </button>
                          <button type="button" className="button clear review-tags__remove" onClick={() => onDrop(r.tagId)}>
                            {t('reference-data:review-tags.drop')}
                          </button>
                        </>
                      )}
                      {state === 'pending' && (
                        <>
                          <span className="review-tags__prompt">{t('reference-data:review-tags.pick-cell')}</span>
                          <button type="button" className="button clear review-tags__cancel" onClick={onCancelReattach}>
                            {t('common:cancel')}
                          </button>
                        </>
                      )}
                      {state === 'placed' && (
                        <>
                          <span className="review-tags__state review-tags__state--placed">{t('reference-data:review-tags.reattached')}</span>
                          <button type="button" className="button clear review-tags__undo" onClick={() => onUndo(r.tagId)}>
                            {t('reference-data:review-tags.undo')}
                          </button>
                        </>
                      )}
                      {state === 'removed' && (
                        <>
                          <span className="review-tags__state review-tags__state--removed">{t('reference-data:review-tags.dropped')}</span>
                          <button type="button" className="button clear review-tags__undo" onClick={() => onUndo(r.tagId)}>
                            {t('reference-data:review-tags.undo')}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
